import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";

const STATUS_STYLE = {
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  not_configured: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function EmailLog() {
  const [emails, setEmails] = useState(null);

  useEffect(() => {
    api.get("/admin/emails").then(({ data }) => setEmails(data)).catch(() => {});
  }, []);

  const clear = async () => {
    if (!window.confirm("Delete all email log entries? This cannot be undone.")) return;
    try {
      const { data } = await api.delete("/admin/emails");
      toast.success(`Cleared ${data.deleted} log entries`);
      setEmails([]);
    } catch {
      toast.error("Failed to clear the log");
    }
  };

  return (
    <div data-testid="email-log-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black">Email log</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Every outgoing email is recorded here — including ones skipped because SMTP isn't configured.
          </p>
        </div>
        <Button variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" onClick={clear} data-testid="clear-email-log-button">
          <Trash2 className="h-4 w-4 mr-1.5" /> Clear log
        </Button>
      </div>
      <div className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E5E5] text-left text-xs uppercase tracking-widest text-neutral-400">
              <th className="px-5 py-3">To</th>
              <th className="px-5 py-3">Subject</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]">
            {!emails ? (
              <tr><td className="px-5 py-6 text-neutral-400" colSpan={4}>Loading…</td></tr>
            ) : emails.length === 0 ? (
              <tr><td className="px-5 py-6 text-neutral-400" colSpan={4} data-testid="emails-empty">No emails sent yet.</td></tr>
            ) : (
              emails.map((e) => (
                <tr key={e.id} data-testid={`email-row-${e.id}`}>
                  <td className="px-5 py-3.5">{e.to}</td>
                  <td className="px-5 py-3.5 text-neutral-600">{e.subject}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs border rounded-full px-2.5 py-1 ${STATUS_STYLE[e.status] || ""}`} data-testid={`email-status-${e.id}`}>
                      {e.status}
                    </span>
                    {e.error && <div className="text-xs text-red-500 mt-1 max-w-xs truncate">{e.error}</div>}
                  </td>
                  <td className="px-5 py-3.5 text-neutral-500">{new Date(e.created_at).toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
